import React, { useState, useEffect } from 'react';

import { useRecoilValue, RecoilState, useSetRecoilState } from 'recoil';
import { Text, FormLabel, FormControl, FormErrorMessage, Input } from '@tq/ui-components';

interface EditableTableCellProps {
  rowValue: string;
  editingState: RecoilState<number | undefined>;
  valueState: RecoilState<string>;
  rowIndex: number;
  type: 'text' | 'email';
  label: string;
  placeholder: string;
  errorMessage?: string;
  validator?: (value: string) => boolean;
  disabled?: boolean;
  dataTestId?: string;
}

export const EditableTableCell: React.FC<EditableTableCellProps> = ({
  rowValue,
  editingState,
  valueState,
  rowIndex,
  type,
  label,
  placeholder,
  errorMessage,
  validator,
  disabled = false,
  dataTestId = '',
}) => {
  const currentEditRowIndex = useRecoilValue(editingState);
  const setRecoilValue = useSetRecoilState(valueState);
  const [value, setValue] = useState<string>(rowValue);
  const [isInputInvalid, setIsInputInvalid] = useState<boolean>(false);

  const onUpdateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const onBlurHandler = () => {
    setRecoilValue(value);
    if (validator) setIsInputInvalid(!validator(value));
  };

  useEffect(() => {
    if (currentEditRowIndex === rowIndex) {
      if (rowValue?.length) {
        setRecoilValue(rowValue);
        setValue(rowValue);
        if (validator) setIsInputInvalid(!validator(rowValue));
      }
    }
  }, [rowValue, currentEditRowIndex, rowIndex, setRecoilValue, validator]);

  return currentEditRowIndex === rowIndex ? (
    <FormControl
      width='100%'
      isInvalid={isInputInvalid && !disabled}
      position='relative'
      top={0}
    >
      <FormLabel htmlFor={`${type}-input`}>{label}:</FormLabel>
      <Input
        id={`${type}-input`}
        type={type}
        width='100%'
        value={value}
        onChange={onUpdateValue}
        onBlur={onBlurHandler}
        mb={0}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={dataTestId}
      />
      <FormErrorMessage role='alert' flexGrow={1}>
        {errorMessage}
      </FormErrorMessage>
    </FormControl>
  ) : (
    <Text>{rowValue}</Text>
  );
};
